import { CURRENCY_INFO, convertMinor, fillRates, formatMoney, fromMinor, toMinor, type CurrencyCode, type Rates } from '@expenditure/core';
import type { TFunction } from 'i18next';
import { ApiError } from './api';

export { formatMoney };

const localeFor = (language: string) => (language === 'km' ? 'km-KH' : 'en-US');

/** Today's date as YYYY-MM-DD in the user's own time zone. */
export function todayIso(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/** "Today", "Yesterday", or a localized date for timeline headers. */
export function formatDayHeader(isoDate: string, language: string, t: TFunction): string {
  const today = todayIso();
  const yesterday = new Date(`${today}T00:00:00`);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayIso = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  if (isoDate === today) return t('group.today');
  if (isoDate === yesterdayIso) return t('group.yesterday');
  return formatDate(isoDate, language);
}

export function formatDate(isoDate: string, language: string): string {
  const date = new Date(`${isoDate}T00:00:00`);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(localeFor(language), {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** Checks a user-typed amount against the currency's precision ("12.345" USD is invalid). */
export function isValidAmountInput(value: string, currency: CurrencyCode): boolean {
  const digits = CURRENCY_INFO[currency].minorDigits;
  const pattern = digits === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`);
  return pattern.test(value.trim()) && Number(value) > 0;
}

/** Turns an API or network error into a user-facing, translated message. */
export function errorMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    const key = `errors.${error.code}`;
    const translated = t(key);
    return translated !== key ? translated : error.message || t('errors.generic');
  }
  if (error instanceof TypeError) return t('errors.network');
  return t('errors.generic');
}

/**
 * The amount field's text after switching currency: converted at the
 * group's rates (55500 ៛ → 13.88 $). Empty or half-typed input is kept.
 */
export function convertAmountInput(amount: string, from: CurrencyCode, to: CurrencyCode, rates: Rates | number): string {
  if (from === to || !isValidAmountInput(amount, from) || amount.trim() === '') return amount;
  try {
    return fromMinor(convertMinor(toMinor(amount, from), from, to, rates), to);
  } catch {
    return amount;
  }
}

/** "0.00" for currencies with cents, "0" for riel, dong, yen and won. */
export const amountPlaceholder = (currency: CurrencyCode): string => (CURRENCY_INFO[currency].minorDigits > 0 ? '0.00' : '0');

/** "25,400₫": how much of `currency` one dollar buys. */
export function formatRate(rate: string | undefined, currency: CurrencyCode): string {
  const info = CURRENCY_INFO[currency];
  const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(Number(rate ?? 0));
  return info.symbolPosition === 'prefix' ? `${info.symbol}${number}` : `${number}${info.symbol}`;
}

/**
 * The rates behind a conversion, one per non-dollar currency involved:
 * "$1 = 25,400₫", or "$1 = ¥150 · $1 = 4,100៛" between yen and riel.
 */
export function rateNote(currencies: CurrencyCode[], rates: Rates): string {
  return [...new Set(currencies)]
    .filter((c) => c !== 'USD')
    .map((c) => `$1 = ${formatRate(fillRates(rates)[c as Exclude<CurrencyCode, 'USD'>], c)}`)
    .join(' · ');
}
